import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { GlobalTable, HeaderLine, Td, Th, Tr } from '../../../../components/styles/table-style'
import { useOutletContext } from 'react-router-dom'
import { FashionOutletType } from '..'
import axios from '../../../../config/axios'
import { LoadMoreProvider, useLoadMore } from '../../../../contexts/LoadMoreContext'
import LoadMoreButton from '../../../../components/LoadMoreButton'
import ActionsDropdown from './ActionsDropdown'
import eventBus from '../../../../utils/eventBus'

const ORDERS_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders`

const OrdersTableContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  width: 100%;
`

const TableScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
`

type OrderType = {
  id: number
  clientId: number
  client: string | null
  createdAt: string
  startDateOfDelivery: string
  orderDate: string
  quantityItems: string
  quantityPieces: string
  status: boolean
  totalCost: number
  total: number
  saleStatus: string
}

const FashionOrdersTableContent: React.FC = () => {
  const { setTabIndex } = useOutletContext<FashionOutletType>()
  const [orders, setOrders] = useState<OrderType[]>([])
  const [currentCount, setCurrentCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const { hasNextPage, setHasNextPage, isFetchingNextPage, setIsFetchingNextPage, setFetchNextPageFunction } = useLoadMore()

  const columns = ["Nº do pedido", "Cliente", "Data pedido", "Data entrega", "Qtd. de itens", "Qtd. de peças", "Status", "Total (R$)", "Venda", ""]

  const fetchData = async (page: number, append: boolean = false) => {
    try {
      if (append) setIsFetchingNextPage(true)

      const size = 50
      const pageOneBased = page + 1
      const response = await axios.get(`${ORDERS_URL}/order/paginated`, {
        params: { page: pageOneBased, limit: size, onlyActive: 'true' }
      })

      const result = response.data
      const ordersData = result.data || []
      const total = result.total || 0

      if (append) {
        setOrders(prev => [...prev, ...ordersData])
        setCurrentCount((prev: number) => prev + ordersData.length)
      } else {
        setOrders(ordersData)
        setCurrentCount(ordersData.length)
        setTotalCount(total)
      }

      const newCurrent = append ? currentCount + ordersData.length : ordersData.length
      setHasNextPage(newCurrent < total)
    } catch (error) {
      console.error('Erro ao buscar os pedidos:', error)
    } finally {
      if (append) setIsFetchingNextPage(false)
    }
  }

  useEffect(() => {
    fetchData(0)
  }, [])

  useEffect(() => {
    const handler = () => fetchData(0)
    eventBus.on('refreshFashionTable', handler)
    return () => eventBus.off('refreshFashionTable', handler)
  }, [])

  useEffect(() => {
    setFetchNextPageFunction(() => fetchData(Math.floor(currentCount / 50), true))
  }, [currentCount])

  const hasMore = currentCount < totalCount

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'N/A'
    try {
      const d = new Date(dateStr)
      return d.toLocaleDateString('pt-BR')
    } catch {
      return 'N/A'
    }
  }

  return (
    <OrdersTableContainer>
      <TableScroll>
        <GlobalTable>
          <thead>
            <HeaderLine>
              {columns.map((item, index) => (
                <Th key={index}>{item}</Th>
              ))}
            </HeaderLine>
          </thead>
          <tbody>
            {orders.map((order) => (
              <Tr key={order.id}>
                <Td>{order.id}</Td>
                <Td>{order.client || 'N/A'}</Td>
                <Td>{formatDate(order.orderDate || order.createdAt)}</Td>
                <Td>{formatDate(order.startDateOfDelivery)}</Td>
                <Td>{order.quantityItems || '0'}</Td>
                <Td>{order.quantityPieces || '0'}</Td>
                <Td>{order.status ? 'Ativo' : 'Inativo'}</Td>
                <Td>R${(Number(order.totalCost) || Number(order.total) || 0).toFixed(2)}</Td>
                <Td>{order.saleStatus || '-'}</Td>
                <Td>
                  <ActionsDropdown
                    order={order}
                    onDelete={() => setOrders((prev: OrderType[]) => prev.filter((o) => o.id !== order.id))}
                    onUpdate={() => fetchData(0)}
                  />
                </Td>
              </Tr>
            ))}
          </tbody>
        </GlobalTable>

        <LoadMoreButton
          currentCount={currentCount}
          totalCount={totalCount}
          hasMore={hasMore}
          onLoadMore={() => fetchData(Math.floor(currentCount / 50), true)}
          isLoading={isFetchingNextPage}
        />
      </TableScroll>
    </OrdersTableContainer>
  )
}

const FashionOrdersTable: React.FC = () => {
  return (
    <LoadMoreProvider>
      <FashionOrdersTableContent />
    </LoadMoreProvider>
  )
}

export default FashionOrdersTable
